import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { download } from '../../api/http'
import { rpc, useRpcEvent } from '../../api/rpc'
import { formatBytes, formatDateTime } from '../../lib/format'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { useToast } from '../ui/Toast'

export interface ChatArtifact { path: string; kind: 'create' | 'edit'; updatedAt: number; size: number | null }

/** Header button + dialog listing the files this chat's agent created or edited (F2.11). */
export function ChatFiles({ chatId, workspaceId }: { chatId: string; workspaceId?: string }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [items, setItems] = useState<ChatArtifact[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    void rpc.call<{ items: ChatArtifact[] }>('chats.artifacts', { chatId }).then(r => setItems(r.items)).catch(() => undefined)
  }, [chatId])
  useRpcEvent('chat.artifacts', (p: { chatId: string; items: ChatArtifact[] }) => { if (p.chatId === chatId) setItems(p.items) })

  const name = (p: string) => p.split('/').pop() ?? p
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} aria-label={t('chat.filesTitle')}>
        {t('chat.files')}{items.length > 0 && <span className="ml-1.5 rounded-full bg-cream-200 px-1.5 text-[11px] font-medium tabular-nums text-ink-500">{items.length}</span>}
      </Button>
      <Modal open={open} title={t('chat.filesTitle')} onClose={() => setOpen(false)}>
        {items.length === 0 ? <p className="text-sm text-ink-500">{t('chat.filesEmpty')}</p> : (
          <ul className="divide-y divide-line">
            {items.map(f => (
              <li key={f.path} className="flex items-center gap-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium" title={f.path}>{name(f.path)}</p>
                  <p className="truncate text-xs text-ink-500">
                    {f.path} · {f.kind === 'create' ? t('chat.created') : t('chat.edited')} {formatDateTime(f.updatedAt, i18n.language)}
                    {f.size === null ? ` · ${t('chat.fileGone')}` : ` · ${formatBytes(f.size)}`}
                  </p>
                </div>
                {f.size !== null && (
                  <Button variant="secondary" size="sm"
                    onClick={() => void download(`/api/files/download?path=${encodeURIComponent(f.path)}${workspaceId ? `&workspace=${encodeURIComponent(workspaceId)}` : ''}`, name(f.path)).catch(err => toast((err as Error).message, 'error'))}>
                    {t('files.download')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  )
}
